import React, { useState, useRef, useEffect } from 'react';
import { ViewContainer } from '../BracketContestants.styles';
import NewGameState from '../../../_types/NewGameState';
import Contestant from '../../../../_types/Contestant';
import CustomModal from '../../../custom-inputs/CustomModal';
import SelectBracketContestants from '../SelectBracketContestants';
import ContestantList from '../ContestantList';
import styled from '@emotion/styled';
import { GamifyIcon } from '../../../../../../components/GamifyIcon';
import {
  CustomInputLabel,
  ContestantPill,
  AddButton,
} from '../../../custom-inputs/CustomInputs.styles';
import { CustomTheme } from 'application/context/PermissionsContext/ThemeContext/themesTypes';
import { useTheme } from 'application/context/PermissionsContext/ThemeContext/ThemeContext';

// Styled components with modern design
const BracketContainer = styled.div<{ theme?: CustomTheme }>`
  background-color: ${({ theme }) => theme?.custom?.background1 || '#ffffff'};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme?.custom?.stroke || '#e0e0e0'};

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const ContestantCount = styled.div<{ theme?: CustomTheme }>`
  font-size: 14px;
  font-weight: 600;
  margin-top: 12px;
  color: ${({ theme }) => theme.custom.text};

  @media (min-width: 768px) {
    margin-top: 0;
  }
`;

const BackButton = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 10px;
  margin-bottom: 20px;
  border-radius: 8px;
  transition: background-color 0.2s;

  &:hover {
    opacity: 0.5;
  }

  & > div {
    font-weight: 600;
    font-size: 16px;
  }
`;

interface Props {
  newGameState: NewGameState;
  onChangeNewGameState: (newGameState: NewGameState) => void;
  contestants: Contestant[];
  selectedContestants: string[];
}

const BracketContestantsView: React.FC<Props> = ({
  newGameState,
  contestants,
  onChangeNewGameState,
  selectedContestants,
}) => {
  const [showContestantsBracketModal, setShowContestantsBracketModal] =
    useState(false);
  const [showContestantList, setShowContestantList] = useState(false);
  const [currentSeedIndexToEdit, setCurrentSeedIndexToEdit] =
    useState<any>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const contestantListRef = useRef<HTMLDivElement>(null);

  const { theme } = useTheme();

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    setScrollPosition(target.scrollTop);
  };

  const restoreScrollPosition = () => {
    if (contestantListRef.current && scrollPosition > 0) {
      requestAnimationFrame(() => {
        if (contestantListRef.current) {
          contestantListRef.current.scrollTop = scrollPosition;
        }
      });
    }
  };

  useEffect(() => {
    if (showContestantList) {
      restoreScrollPosition();
    }
  }, [showContestantList]);

  const renderBracketInfo = () => (
    <>
      <CustomInputLabel>Bracket Contestants</CustomInputLabel>
      <BracketContainer>
        <ContestantPill
          backgroundColor="#FF8A65"
          onClick={() => {
            setShowContestantsBracketModal(true);
          }}
        >
          <AddButton>
            <GamifyIcon icon="add" color="white" height={16} width={16} />
            Add
          </AddButton>
        </ContestantPill>

        {selectedContestants.length > 0 && (
          <ContestantCount>
            {selectedContestants.length} contestants selected
          </ContestantCount>
        )}
      </BracketContainer>
    </>
  );

  const renderModalContent = () =>
    showContestantList ? (
      <>
        <BackButton onClick={() => setShowContestantList(false)}>
          <GamifyIcon
            icon="leftArrow"
            color={theme.text}
            height={16}
            width={16}
          />
        </BackButton>
        <div
          ref={contestantListRef}
          onScroll={handleScroll}
          style={{
            maxHeight: '400px',
            overflowY: 'auto',
            marginTop: '20px',
          }}
        >
          <ContestantList
            contestants={contestants}
            selectedContestants={selectedContestants}
            onSelect={(contestant: Contestant) => {
              const newGameStateCopy = { ...newGameState };
              newGameStateCopy.competitors.splice(
                currentSeedIndexToEdit,
                1,
                contestant.value
              );
              onChangeNewGameState(newGameStateCopy);
              setShowContestantList(false);
            }}
          />
        </div>
      </>
    ) : (
      <SelectBracketContestants
        newGameState={newGameState}
        contestants={contestants}
        totalNumberOfContestants={newGameState.bracket.totalCompetitors}
        onClickAdd={(args: { seedIndex: number }) => {
          const { seedIndex } = args;
          setCurrentSeedIndexToEdit(seedIndex);
          setShowContestantList(true);
        }}
        onSubmit={() => setShowContestantsBracketModal(false)}
      />
    );

  return (
    <ViewContainer>
      {renderBracketInfo()}

      <CustomModal
        width={1000}
        isOpen={showContestantsBracketModal}
        title="Edit Contestants"
        onClose={() => setShowContestantsBracketModal(false)}
      >
        {renderModalContent()}
      </CustomModal>
    </ViewContainer>
  );
};

export default BracketContestantsView;
